import { useRef, memo, ReactElement } from 'react'
import { CardHeader, CardContent, styled } from '@mui/material'
import { Typography } from '@carto/meridian-ds/components'
import CardMiniIcon, { CardMiniIconProps } from './CardMiniIcon'
import useHasMultiLine from 'features/common/ui/utils/useHasMultiLine'

const Root = styled('div', {
  shouldForwardProp: (prop) => prop !== 'hasPadding' && prop !== 'minHeight'
})<{ hasPadding?: boolean; minHeight?: number | string }>(({ hasPadding, minHeight, theme }) => ({
  minHeight: minHeight ?? 'auto',
  borderTop: `1px solid ${theme.palette.black['4']}`,

  ...(hasPadding && {
    padding: theme.spacing(2, 2.5)
  })
}))

const Header = styled(CardHeader, {
  shouldForwardProp: (prop) => prop !== 'hasMultipleLines'
})<{ hasMultipleLines?: boolean }>(({ hasMultipleLines, theme }) => ({
  padding: 0,

  '& .MuiCardHeader-content': {
    width: `calc(100% - ${theme.spacing(4.5)})`
  },
  '& .MuiTypography-root': {
    display: '-webkit-box',
    wordBreak: 'break-word',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  },
  '& .MuiCardHeader-action': {
    marginRight: theme.spacing(-1.5)
  },

  ...(!hasMultipleLines && {
    '& .MuiTypography-root': {
      display: 'block',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      marginBottom: theme.spacing(0.5)
    }
  })
}))

const Content = styled(CardContent)(({ theme }) => ({
  '&, &:last-child': {
    padding: theme.spacing(0)
  }
}))

const Description = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'hasMultipleLines'
})<{ hasMultipleLines?: boolean }>(({ hasMultipleLines }) => ({
  display: '-webkit-box',

  ...(!hasMultipleLines && {
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden'
  })
}))

interface CardBasicInformationProps {
  title?: string
  description?: string | null
  action?: ReactElement | null
  headerContentHasPadding?: boolean
  minHeight?: number | string
  miniIconProps?: CardMiniIconProps
}

function CardBasicInformation({
  title,
  description,
  action,
  headerContentHasPadding = true,
  minHeight,
  miniIconProps
}: CardBasicInformationProps) {
  const titleRef = useRef<HTMLElement>(null)
  const titleHasMultiline = useHasMultiLine({ elementRef: titleRef, lineHeight: 30 })

  return (
    <Root minHeight={minHeight} hasPadding={headerContentHasPadding}>
      <div style={{ position: 'relative' }}>
        {miniIconProps && <CardMiniIcon {...miniIconProps} />}

        <Header
          title={title}
          titleTypographyProps={{
            variant: 'subtitle1',
            ref: titleRef
          }}
          hasMultipleLines={titleHasMultiline}
          action={action}
        />
      </div>

      {!!description && (
        <Content>
          <Description
            variant="body2"
            color="textSecondary"
            hasMultipleLines={titleHasMultiline}
            {...(titleHasMultiline && { noWrap: true })}
          >
            {description}
          </Description>
        </Content>
      )}
    </Root>
  )
}

export default memo(CardBasicInformation)
